import { HStack, Stack, Text } from "@chakra-ui/react";
import * as React from "react";
import { LuCalendar, LuHistory, LuPlus, LuShieldBan } from "react-icons/lu";

import {
  deleteSuppressionAudience,
  getSuppressionAudiences,
} from "../api/suppression-audience";
import { useBreadcrumbs } from "../components/breadcrumbs";
import { Button } from "../components/button";
import { CreateSuppressionAudienceDialog } from "../components/create-suppression-audience-dialog";
import DataTable, { DataTableColumn } from "../components/data-table";
import { DeleteButton } from "../components/delete-button";
import { EmptyState } from "../components/empty-state";
import IconCircle from "../components/icon-circle";
import Page from "../components/page";
import PageBody from "../components/page-body";
import PageHeader from "../components/page-header";
import { SuppressionAudienceSyncedContactsDialog } from "../components/suppression-audience-synced-contacts-dialog";
import {
  useAppDispatch,
  useAppSelector,
  useLoading,
  useToaster,
  useWorkspaceURL,
} from "../hooks";
import { loadIntegrations } from "../store/workspaceSlice";
import { SuppressionAudience } from "../types/suppression-audience";

export default function SuppressionAudiencesPage() {
  const dispatch = useAppDispatch();
  const workspaceSlug = useAppSelector((s) => s.workspace.slug);
  const workspaceURL = useWorkspaceURL();
  useBreadcrumbs([
    { label: "Dashboard", to: workspaceURL("") },
    { label: "Exclusion Audiences" },
  ]);
  const [audiences, setAudiences] = React.useState<SuppressionAudience[]>([]);
  const [isLoading, withLoading] = useLoading();
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [syncedContactsAudience, setSyncedContactsAudience] =
    React.useState<SuppressionAudience | null>(null);
  const withToaster = useToaster();

  React.useEffect(() => {
    dispatch(loadIntegrations());
  }, [dispatch]);

  const loadAudiences = React.useCallback(async () => {
    if (!workspaceSlug) return;
    await withLoading(async () => {
      await withToaster(async () => {
        const data = await getSuppressionAudiences(workspaceSlug);
        setAudiences(data);
        setHasLoadedOnce(true);
      });
    });
  }, [withLoading, withToaster, workspaceSlug]);

  React.useEffect(() => {
    loadAudiences();
  }, [loadAudiences]);

  const handleDelete = React.useCallback(
    async (id: number) => {
      if (!workspaceSlug) return;
      await withLoading(async () => {
        await withToaster(async () => {
          await deleteSuppressionAudience(workspaceSlug, id);
          await loadAudiences();
        });
      });
    },
    [loadAudiences, withLoading, withToaster, workspaceSlug],
  );

  const columns: DataTableColumn<SuppressionAudience>[] = [
    {
      id: "name",
      name: "Name",
      icon: <LuShieldBan />,
      width: 240,
      cell: ({ value }) => <Text>{value.name}</Text>,
    },
    {
      id: "status",
      name: "Status",
      width: 100,
      cell: ({ value }) => (
        <Text textTransform="capitalize">{value.status}</Text>
      ),
    },
    {
      name: "Created",
      icon: <LuCalendar />,
      width: 140,
      cell: ({ value }) => (
        <Text>{new Date(value["created-at"]).toLocaleDateString()}</Text>
      ),
    },
    {
      name: "",
      width: 100,
      cell: ({ value }) => (
        <HStack justify="flex-end">
          <Button
            variant="ghost"
            size="sm"
            color="fg"
            onClick={() => setSyncedContactsAudience(value)}
            title="View synced contacts"
          >
            <LuHistory />
          </Button>
          <DeleteButton
            label="Delete"
            message={`Delete "${value.name}"?`}
            onDelete={() => handleDelete(value.id)}
          />
        </HStack>
      ),
    },
  ];

  const isEmpty = audiences.length === 0 && hasLoadedOnce;

  return (
    <Page loading={isLoading && !hasLoadedOnce}>
      <Stack flex="1">
        <PageHeader title="Exclusion Audiences">
          {!isEmpty && (
            <Button onClick={() => setIsDialogOpen(true)}>
              <LuPlus /> Create Exclusion Audience
            </Button>
          )}
        </PageHeader>
        <PageBody>
          {isEmpty ? (
            <HStack flex="1" alignItems="center">
              <EmptyState
                icon={
                  <IconCircle Icon={LuShieldBan} size="md" variant="flat" />
                }
                title="No Exclusion Audiences"
                description="Create an exclusion audience to exclude contacts from your ad campaigns."
              >
                <Button onClick={() => setIsDialogOpen(true)}>
                  <LuPlus /> Create Exclusion Audience
                </Button>
              </EmptyState>
            </HStack>
          ) : (
            <DataTable
              data={audiences}
              columns={columns}
              skeleton={isLoading && hasLoadedOnce ? 3 : undefined}
            />
          )}
        </PageBody>
      </Stack>
      <CreateSuppressionAudienceDialog
        isOpen={isDialogOpen}
        onOpenChange={({ open }) => setIsDialogOpen(open)}
        onSuccess={loadAudiences}
        workspaceSlug={workspaceSlug ?? ""}
      />
      <SuppressionAudienceSyncedContactsDialog
        audience={syncedContactsAudience}
        isOpen={syncedContactsAudience !== null}
        onOpenChange={({ open }) => {
          if (!open) setSyncedContactsAudience(null);
        }}
        workspaceSlug={workspaceSlug ?? ""}
      />
    </Page>
  );
}
